import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'
import {
  contactEmail,
  feedbackEmail,
  physicalAddress,
  socialLinks,
  socialIconSize,
  type SocialLinkData,
} from '#app/utils/global.ts'
import { cn } from '#app/utils/misc.tsx'
import { checkForTrainingMode } from '#app/utils/training.ts'
import { useOptionalUser } from '#app/utils/user.ts'

function Logo({ width }: { width: number }) {
  const user = useOptionalUser()
  const link = user ? '/dashboard' : '/'

  return (
    <Link to={link}>
      <span className="sr-only">SparkLing</span>
      <img
        srcSet="/img/logo-horizontal.svg"
        src="/img/logo-horizontal.svg"
        alt=""
        width={width}
      />
    </Link>
  )
}

function SocialLinkList() {
  return (
    <ul className="flex items-center gap-4">
      {socialLinks.map(socialLink => (
        <SocialLink key={socialLink.name} {...socialLink} />
      ))}
    </ul>
  )
}

function SocialLink({ name, href, imgPath }: SocialLinkData) {
  return (
    <li>
      <a href={href} aria-label={name} target="_blank">
        <img
          src={imgPath}
          width={socialIconSize}
          height={socialIconSize}
          alt=""
        />
      </a>
    </li>
  )
}

function About() {
  const { t } = useTranslation()

  return (
    <>
      <span>&copy; {new Date().getFullYear()}</span>
      &nbsp;
      <span>{t('footer.about')}</span>
    </>
  )
}

function LinkList({
  listClassName,
  itemClassName,
}: {
  listClassName?: string
  itemClassName?: string
}) {
  const user = useOptionalUser()
  const { t } = useTranslation()

  const itemBaseClassName = 'border-r border-black-default'

  return (
    <ul className={cn('flex', listClassName)}>
      <li className={cn(itemBaseClassName, itemClassName)}>
        <Link to={`mailto:${contactEmail}`}>{t('contactUs')}</Link>
      </li>
      {user && (
        <li className={cn(itemBaseClassName, itemClassName)}>
          <Link to={`mailto:${feedbackEmail}`}>{t('giveFeedback')}</Link>
        </li>
      )}
      <li className={cn(itemBaseClassName, itemClassName)}>
        <Link to="/terms">{t('termsAndConditions')}</Link>
      </li>
      <li>
        <Link to="/privacy">{t('privacyPolicy')}</Link>
      </li>
    </ul>
  )
}

function DesktopFooter() {
  return (
    <div className="container hidden justify-between py-6 lg:flex">
      <div className="max-w-[500px]">
        <div>
          <Logo width={162} />
        </div>
        <div className="mt-5">
          <About />
        </div>
        <div className="mt-4">{physicalAddress}</div>
      </div>
      <div className="flex flex-col items-end justify-end">
        <SocialLinkList />
        <LinkList listClassName="mt-4 gap-4" itemClassName="pr-4" />
      </div>
    </div>
  )
}

function MobileFooter() {
  return (
    <div className="flex flex-col justify-center gap-5 px-8 py-9 text-center lg:hidden">
      <div className="flex justify-center">
        <Logo width={138} />
      </div>
      <div className="flex justify-center">
        <SocialLinkList />
      </div>
      <div className="m-auto max-w-[500px]">
        <About />
      </div>
      <div>{physicalAddress}</div>
      <LinkList
        listClassName="flex-wrap gap-2 justify-center"
        itemClassName="pr-2"
      />
    </div>
  )
}

function Footer() {
  const params = useParams()

  const inTrainingMode = checkForTrainingMode(params)
  if (inTrainingMode) return null

  return (
    <footer className="text-1 text-mauve-11 z-10 bg-transparent">
      <MobileFooter />
      <DesktopFooter />
    </footer>
  )
}

export { Footer }
